import React, { useState } from 'react';
import {
  Shield,
  TestTube,
  Bug,
  CheckCircle2,
  ArrowRight,
  Gauge,
  Terminal,
  Activity,
  FileCode2,
  Globe,
  Smartphone,
  Lock,
  Cpu,
  Layers,
  Search,
  Sliders,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';

const QATesting = () => {
  const [activeStack, setActiveStack] = useState('automation');
  const [activeStep, setActiveStep] = useState(0);

  const qaPillars = [
    {
      icon: TestTube,
      title: 'Automated E2E & Regression Testing',
      badge: 'Continuous Testing',
      color: 'from-cyan-500 to-blue-600',
      description: 'End-to-end automated test suites built with Playwright, Cypress, and Selenium that run inside CI/CD pipelines on every code push.',
      bullets: ['Playwright & Cypress E2E Suites', 'Automated Regression Trigger', 'Parallel Test Runner Execution']
    },
    {
      icon: FileCode2,
      title: 'API & Microservice Testing',
      badge: 'Zero Contract Drift',
      color: 'from-purple-500 to-indigo-600',
      description: 'Validation of RESTful and GraphQL API contracts using Postman, Newman, and RestAssured to verify payload structures and status codes.',
      bullets: ['OpenAPI & GraphQL Schema Specs', 'Postman & Newman Automated Runners', 'Mock API Dependency Injection']
    },
    {
      icon: Gauge,
      title: 'Performance, Stress & Load Testing',
      badge: '10k+ Concurrency',
      color: 'from-orange-500 to-amber-500',
      description: 'High-concurrency load testing powered by k6 and Apache JMeter to simulate peak flash sale spikes, memory leaks, and database bottlenecks.',
      bullets: ['k6 & JMeter Traffic Simulation', 'Latency & Throughput Telemetry', 'Database Query Bottleneck Audit']
    },
    {
      icon: Lock,
      title: 'Security & Vulnerability Auditing',
      badge: 'OWASP Top 10',
      color: 'from-rose-500 to-pink-600',
      description: 'Penetration testing and automated security scans targeting SQL injections, XSS vulnerabilities, broken authentication, and CORS misconfigurations.',
      bullets: ['OWASP Top 10 Penetration Audit', 'Dynamic Application Security (DAST)', 'Dependency Vulnerability Scan']
    },
    {
      icon: Smartphone,
      title: 'Cross-Browser & Multi-Device QA',
      badge: 'Real Device Lab',
      color: 'from-emerald-500 to-teal-600',
      description: 'Rigorous cross-device testing across 50+ real iOS and Android smartphones, iPads, and browser engines (Chrome, Safari, Firefox, Edge).',
      bullets: ['BrowserStack & SauceLabs Sync', 'Responsive Screen Breakpoint QA', 'Native Touch & Gesture Testing']
    },
    {
      icon: Sliders,
      title: 'Accessibility & WCAG 2.1 Compliance',
      badge: 'WCAG AA Level',
      color: 'from-blue-500 to-cyan-600',
      description: 'Automated and manual accessibility auditing using Axe-core and Lighthouse to ensure compliance with WCAG 2.1 AA screen reader standards.',
      bullets: ['Axe-Core Automated Audit', 'Screen Reader & Keyboard Nav', 'Color Contrast Ratio Compliance']
    }
  ];

  const qaShowcases = [
    {
      title: 'Automated E2E Test Suite Execution',
      subtitle: 'Playwright & Cypress CI/CD Integration',
      description: 'Our automated test frameworks execute hundreds of user journey assertions in parallel, catching regression bugs before production deployment.',
      image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80',
      badge: 'Automated QA'
    },
    {
      title: 'Real Device & Cross-Browser Testing Lab',
      subtitle: 'Multi-Device Screen Matrix Validation',
      description: 'Validating pixel-perfect layout responsiveness, gesture interactions, and battery efficiency across real iOS, Android, and web browsers.',
      image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80',
      badge: 'Device Lab'
    }
  ];

  const stackSpecs = {
    automation: {
      name: 'Playwright & Cypress Automation',
      desc: 'Modern web test automation tools delivering fast execution, auto-waiting assertions, video recordings, and trace viewer debugging.',
      specs: ['Auto-Waiting Flake Mitigation', 'Parallel Headless Browser Execution', 'Video & DOM Trace Recording', 'Cross-Browser Engine Coverage']
    },
    load: {
      name: 'k6 & JMeter Load Testing',
      desc: 'Developer-centric performance testing tool for benchmarking API latency, memory spikes, and server throughput under extreme stress.',
      specs: ['10,000+ Virtual Users (VUs)', 'Real-Time InfluxDB / Grafana Sync', 'API Spike & Endurance Scenarios', 'CI/CD Performance Quality Gates']
    },
    security: {
      name: 'OWASP Security & Penetration Stack',
      desc: 'Comprehensive vulnerability assessment tools verifying authentication tokens, payload sanitization, and data encryption standards.',
      specs: ['OWASP ZAP Automated Scans', 'Token & Session Hijack Defense', 'Payload Injection Prevention', 'SSL / TLS Certificate Auditing']
    }
  };

  const processStages = [
    {
      step: '01',
      stageName: 'Strategy & Analysis',
      icon: Search,
      title: 'Requirement Analysis & Risk-Based Test Strategy',
      subtitle: 'Phase 01: Scope & Risk Assessment',
      description: 'Our QA architects analyze your product specs, user journeys, and technical architecture to map critical path risk vectors and write a comprehensive QA Master Test Plan.',
      deliverables: ['QA Master Test Plan', 'Risk-Based Test Coverage Matrix', 'Environment Dependency Map', 'Acceptance Criteria Specs'],
      toolCommand: 'qa_strategy.init --risk-matrix=high'
    },
    {
      step: '02',
      stageName: 'Scripting & Automation',
      icon: Code2Icon,
      title: 'Test Case Design & Playwright / Cypress Scripting',
      subtitle: 'Phase 02: Test Suite Development',
      description: 'We craft modular, reusable automated test scripts leveraging Page Object Models (POM), parallel execution fixtures, and automated mock API handlers.',
      deliverables: ['Playwright / Cypress Test Suites', 'Page Object Model (POM) Specs', 'Mock Data Fixtures', 'Visual Regression Baseline'],
      toolCommand: 'npx playwright test --headed --parallel=4'
    },
    {
      step: '03',
      stageName: 'CI/CD Pipeline Sync',
      icon: GitBranch,
      title: 'CI/CD Quality Gates & Automated Regression',
      subtitle: 'Phase 03: Continuous Integration Integration',
      description: 'We integrate automated test runners directly into GitHub Actions or GitLab CI workflows. Every pull request triggers fast headless regression tests before merging.',
      deliverables: ['GitHub Actions / GitLab CI Workflow', 'Automated PR Quality Gates', 'HTML Test Trace Reports', 'Slack / Teams Alert Hooks'],
      toolCommand: 'actions/checkout -> run e2e-tests'
    },
    {
      step: '04',
      stageName: 'Defect Resolution',
      icon: Bug,
      title: 'Defect Triage, Root-Cause Analysis & Fix Verification',
      subtitle: 'Phase 04: Bug Triage & Verification',
      description: 'Every detected bug is logged with full DOM traces, video recordings, console logs, and network payloads for fast developer reproduction and zero-friction re-testing.',
      deliverables: ['Detailed Jira / GitHub Issue Logs', 'DOM & Network Trace Recordings', 'Regression Verification Audits', 'Root Cause Analysis Notes'],
      toolCommand: 'npx playwright show-trace trace.zip'
    },
    {
      step: '05',
      stageName: 'Production Sign-Off',
      icon: Shield,
      title: 'Quality Sign-Off & Performance Benchmark Report',
      subtitle: 'Phase 05: Release Clearance & Telemetry',
      description: 'Upon achieving 100% critical path test pass rates, we issue a formal QA Clearance Certificate along with k6 load test results and OWASP security audit logs.',
      deliverables: ['QA Release Clearance Certificate', 'k6 Load Performance Telemetry', 'OWASP Security Audit Report', 'Production Health Checklist'],
      toolCommand: 'qa_release.authorize --status=APPROVED'
    }
  ];

  function Code2Icon(props) {
    return <FileCode2 {...props} />;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-slate-950 to-gray-950 relative overflow-hidden border-b border-gray-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
                <Shield className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                  Zero-Defect Software Quality
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Software Quality Assurance & <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Automated Testing</span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Eliminate software bugs, boost application performance, and guarantee security compliance with automated E2E testing, API validation, and load stress testing.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Schedule QA Audit
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#qa-stepper">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore QA Process
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live Test Telemetry Console */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Console Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-400 animate-ping" />
                    <span className="text-sm font-mono text-emerald-400 font-bold">TEST SUITE: PASSED</span>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400">PLAYWRIGHT / k6</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Test Pass Rate</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">100%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Code Coverage</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">98.4%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Critical Bugs</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">0 FOUND</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Load Test Max</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">10k VUs</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 font-mono text-sm text-gray-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-emerald-400" />
                  <span>npx playwright test --reporter=html (PASSED)</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* QA Performance SLA Banner */}
      <section className="py-10 bg-gray-900/80 border-y border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">98.4%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Test Code Coverage</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">100%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Critical Path Automation</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">10,000+</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Concurrent Load Users</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">OWASP</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Security Top 10 Verified</div>
            </div>
          </div>
        </div>
      </section>

      {/* QA Visual Showcase Images Gallery */}
      <section className="py-24 bg-gray-900/60 border-b border-gray-800/80">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                Visual Testing & Automation Gallery
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Our Testing <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Environment & Labs</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Inside our automated testing pipelines and real multi-device testing environments.
            </p>
          </div>

          {/* 2-Card Visual Showcase */}
          <div className="grid md:grid-cols-2 gap-8">
            {qaShowcases.map((showcase, idx) => (
              <div key={idx} className="group bg-gray-900 border border-gray-800 hover:border-cyan-500/60 rounded-3xl overflow-hidden shadow-2xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={showcase.image}
                    alt={showcase.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent" />
                  <div className="absolute top-4 left-4 bg-gray-950/90 border border-cyan-500/30 text-cyan-300 text-sm font-bold px-3.5 py-1.5 rounded-full">
                    {showcase.badge}
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                    {showcase.title}
                  </h3>
                  <div className="text-sm text-orange-400 font-semibold mb-4">{showcase.subtitle}</div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {showcase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* QA Pillars Grid */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Full-Spectrum Testing Core
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Comprehensive <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">QA Capabilities</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From automated E2E Playwright scripts to performance load testing and security penetration audits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qaPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-cyan-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive Stepper Concept: Our 5-Step QA Execution Process */}
      <section id="qa-stepper" className="py-24 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                Interactive Stage Stepper
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Our 5-Step <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">QA Execution Pipeline</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Click any pipeline stage below to explore its specific QA deliverables, automated test scripts, and quality sign-off criteria.
            </p>
          </div>

          {/* Top Horizontal Stage Selector Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {processStages.map((stage, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`px-5 py-3 rounded-2xl border text-sm font-bold transition-all duration-300 flex items-center space-x-2.5 ${activeStep === idx
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 border-cyan-400 text-white shadow-xl shadow-cyan-500/20 scale-105'
                  : 'bg-gray-900/80 border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'
                  }`}
              >
                <span className="font-mono text-cyan-300 font-black">{stage.step}</span>
                <span>{stage.stageName}</span>
              </button>
            ))}
          </div>

          {/* Active Stage Spotlight Display Card */}
          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden backdrop-blur-xl">

            {/* Stage Top Bar */}
            <div className="flex flex-wrap items-center justify-between pb-6 border-b border-gray-800 mb-8 gap-4">
              <div className="flex items-center space-x-3">
                <span className="text-4xl font-black text-cyan-400 font-mono">
                  {processStages[activeStep].step}
                </span>
                <div>
                  <div className="text-sm text-orange-400 font-bold uppercase tracking-wider">
                    {processStages[activeStep].subtitle}
                  </div>
                  <h3 className="text-2xl font-extrabold text-white">
                    {processStages[activeStep].title}
                  </h3>
                </div>
              </div>

              {/* Terminal Command Pill */}
              <div className="bg-gray-950 border border-gray-800 px-4 py-2 rounded-xl font-mono text-sm text-cyan-300 flex items-center gap-2">
                <Terminal className="w-4 h-4 text-emerald-400" />
                <span>{processStages[activeStep].toolCommand}</span>
              </div>
            </div>

            {/* Narrative */}
            <p className="text-gray-300 text-base leading-relaxed mb-8">
              {processStages[activeStep].description}
            </p>

            {/* Deliverables Checklist Grid */}
            <div className="mb-10">
              <div className="text-sm text-gray-400 font-bold uppercase tracking-wider mb-4">Stage Key Deliverables</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {processStages[activeStep].deliverables.map((item, i) => (
                  <div key={i} className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl flex items-center space-x-3">
                    <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-200">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prev / Next Stage Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-800">
              <button
                onClick={() => setActiveStep((prev) => (prev === 0 ? processStages.length - 1 : prev - 1))}
                className="inline-flex items-center text-sm font-bold text-gray-400 hover:text-white bg-gray-950 border border-gray-800 px-5 py-2.5 rounded-xl transition-all"
              >
                <ChevronLeft className="w-4 h-4 mr-1.5 text-cyan-400" />
                Previous Stage
              </button>

              <div className="text-sm text-gray-400 font-mono">
                STAGE {activeStep + 1} OF 5
              </div>

              <button
                onClick={() => setActiveStep((prev) => (prev + 1) % processStages.length)}
                className="inline-flex items-center text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 px-5 py-2.5 rounded-xl shadow-md transition-all"
              >
                Next Stage
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* High-Tech Full-Width QA Command Strip CTA */}
      <section className="py-20 bg-gradient-to-r from-gray-950 via-slate-900 to-gray-950 relative overflow-hidden border-t border-cyan-500/30 w-full shadow-2xl">
        <div className="container mx-auto px-6 relative z-10">
          <div className="bg-gradient-to-r from-slate-900/90 via-gray-900/90 to-slate-900/90 border border-gray-800 hover:border-cyan-500/50 rounded-3xl p-8 md:p-12 shadow-2xl backdrop-blur-xl">
            <div className="grid lg:grid-cols-12 gap-8 items-center">

              {/* Left Column Copy */}
              <div className="lg:col-span-7 space-y-4 text-left">
                <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3.5 py-1">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 text-[11px] font-mono font-bold uppercase tracking-widest">
                    ZERO CRITICAL DEFECT GUARANTEE
                  </span>
                </div>

                <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
                  Need Automated QA & <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Performance Load Testing?</span>
                </h2>

                <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                  Deploy Playwright / Cypress E2E suites, k6 10k+ user load testing, and OWASP security audits with our senior Quality Assurance engineers.
                </p>

                <div className="flex flex-wrap gap-3 pt-2">
                  <div className="flex items-center space-x-2 bg-gray-950 border border-gray-800 px-3.5 py-1.5 rounded-xl text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                    <span>98.4% Code Coverage SLA</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-950 border border-gray-800 px-3.5 py-1.5 rounded-xl text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-orange-400" />
                    <span>10k VUs Stress Tested</span>
                  </div>
                </div>
              </div>

              {/* Right Column Action Card */}
              <div className="lg:col-span-5 flex flex-col items-center lg:items-end justify-center">
                <div className="bg-gray-950 border border-gray-800 rounded-2xl p-6 w-full max-w-sm text-center space-y-4 shadow-xl">
                  <div className="text-sm font-mono text-cyan-400 uppercase tracking-widest font-bold">
                    QA CONSULTATION APPOINTMENT
                  </div>
                  <a href="/contact" className="block w-full">
                    <Button className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all flex items-center justify-center gap-2">
                      <Shield className="w-5 h-5" />
                      Schedule QA Strategy Meeting
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                  <div className="text-[11px] text-gray-400 font-mono">
                    Includes 30-Min Automated Testing Audit
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

    </div>
  );
};

export default QATesting;